import { Injectable, signal } from '@angular/core';

export interface RoleInfo {
  id: string;
  name: string;
}

export interface LoggedUser {
  id: string;
  email: string;
  firstname: string;
  lastname: string;
  roleId: string;
  roleName: string;
  status: string;
  isActive: boolean;
  permissions: string[];
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'agefop_access_token',
  REFRESH_TOKEN: 'agefop_refresh_token',
  USER: 'agefop_user',
  PERMISSIONS: 'agefop_permissions',
  REMEMBER_ME: 'agefop_remember_me',
};

@Injectable({ providedIn: 'root' })
export class AuthStore {
  readonly accessToken = signal<string | null>(this.loadAccessToken());
  readonly refreshToken = signal<string | null>(this.loadRefreshToken());
  readonly user = signal<LoggedUser | null>(this.loadUser());
  readonly permissions = signal<string[]>(this.loadPermissions());
  readonly isAuthenticated = signal<boolean>(!!this.accessToken());

  setSession(data: { accessToken: string; refreshToken: string; user: LoggedUser }): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(data.user.permissions));
    }
    this.accessToken.set(data.accessToken);
    this.refreshToken.set(data.refreshToken);
    this.user.set(data.user);
    this.permissions.set(data.user.permissions);
    this.isAuthenticated.set(true);
  }

  updateUser(user: LoggedUser): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(user.permissions));
    }
    this.user.set(user);
    this.permissions.set(user.permissions);
  }

  clearSession(): void {
    if (typeof window !== 'undefined') {
      const keys = Object.values(STORAGE_KEYS);
      keys.forEach(k => localStorage.removeItem(k));
    }
    this.accessToken.set(null);
    this.refreshToken.set(null);
    this.user.set(null);
    this.permissions.set([]);
    this.isAuthenticated.set(false);
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  getRefreshToken(): string | null {
    return this.refreshToken();
  }

  getToken(): string | null {
    return this.accessToken();
  }

  getUser(): LoggedUser | null {
    return this.user();
  }

  isLoggedIn(): boolean {
    return this.isAuthenticated();
  }

  setRememberMe(value: boolean): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(STORAGE_KEYS.REMEMBER_ME, value ? 'true' : 'false');
  }

  getRememberMe(): boolean {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
  }

  updateTokens(accessToken: string, refreshToken: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    }
    this.accessToken.set(accessToken);
    this.refreshToken.set(refreshToken);
  }

  private loadAccessToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  private loadRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  private loadUser(): LoggedUser | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_KEYS.USER);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LoggedUser;
    } catch {
      return null;
    }
  }

  private loadPermissions(): string[] {
    if (typeof window === 'undefined') return [];
    const raw = localStorage.getItem(STORAGE_KEYS.PERMISSIONS);
    if (!raw) return [];
    try {
      return JSON.parse(raw) as string[];
    } catch {
      return [];
    }
  }
}
